import { useState, useEffect, useRef, useMemo, type ReactNode } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useSearchStore } from '@/stores/searchStore'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getMyHistory, getMyAttendanceOverview, type MyAttendanceRecord } from '@/api/attendance'
import { format, parseISO } from 'date-fns'
import AttendanceCalendar from '@/components/dashboard/AttendanceCalendar'
import { LogIn, LogOut, Clock, X, Calendar, Home, ChevronRight, ChevronLeft } from 'lucide-react'
import { createLeave } from '@/api/leave'
import { useCheckInOut } from '@/hooks/useCheckInOut'
import CheckinModal from '@/components/CheckinModal'
import CheckoutModal from '@/components/CheckoutModal'
import EarlyLeavePrompt from '@/components/EarlyLeavePrompt'

/* ─── Shared UI tokens (brand palette) ─── */
const BRAND = '#EC5B2C'
const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-[#EC5B2C] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#d54e22] transition-colors disabled:opacity-50'
const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
const btnDanger =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors disabled:opacity-50'

function hoursMins(totalMin?: number): string {
  const m = Math.max(0, Math.round(totalMin || 0))
  const h = Math.floor(m / 60)
  return h > 0 ? `${h}h ${m % 60}m` : `${m % 60}m`
}

function shiftMinutes(standup?: string, eod?: string): number {
  const [sh, sm] = (standup || '09:00').split(':').map(Number)
  const [eh, em] = (eod || '17:00').split(':').map(Number)
  const start = sh * 60 + sm
  const end = eh * 60 + em
  const raw = end === start ? 8 * 60 : end < start ? 24 * 60 - start + end : end - start
  return Math.max(raw, 60)
}

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function weekRange(dateStr: string): [string, string] {
  const d = new Date(dateStr + 'T00:00:00')
  const dow = (d.getDay() + 6) % 7 // Mon=0
  const monday = new Date(d)
  monday.setDate(d.getDate() - dow)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return [isoLocal(monday), isoLocal(sunday)]
}

function monthRange(dateStr: string): [string, string] {
  const [y, m] = dateStr.split('-').map(Number)
  return [`${y}-${String(m).padStart(2, '0')}-01`, isoLocal(new Date(y, m, 0))]
}

function formatTime(isoStr?: string): string {
  if (!isoStr) return '—'
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function calcDuration(checkinTime?: string, checkoutTime?: string): string {
  if (!checkinTime) return '—'
  const start = new Date(checkinTime).getTime()
  const end = checkoutTime ? new Date(checkoutTime).getTime() : Date.now()
  const diff = end - start
  if (diff <= 0) return '—'
  const hours = Math.floor(diff / 3600000)
  const mins = Math.floor((diff % 3600000) / 60000)
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
}

/* ─── Shift Clock ─── */
function ShiftClock({
  isCheckedOut,
  idle,
  totalWorkedMinutes,
  effectiveStandupTime,
  effectiveEodTime,
}: {
  isCheckedOut?: boolean
  idle?: boolean
  totalWorkedMinutes?: number
  effectiveStandupTime?: string
  effectiveEodTime?: string
}) {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (isCheckedOut) return // stop ticking after checkout
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [isCheckedOut])

  // Compute shift duration in minutes from effective times (overnight-aware)
  const startParts = (effectiveStandupTime || '09:00').split(':').map(Number)
  const endParts = (effectiveEodTime || '17:00').split(':').map(Number)
  const startMin = startParts[0] * 60 + startParts[1]
  const endMin = endParts[0] * 60 + endParts[1]
  const rawShiftMin = endMin === startMin
    ? 0
    : endMin < startMin
      ? 24 * 60 - startMin + endMin  // overnight shift
      : endMin - startMin
  const totalShift = Math.max(rawShiftMin, 60) // at least 1h

  // Use cumulative total_worked_minutes from backend (sums all sessions today)
  // Add ~tick*0.5 min to approximate live progress between refetches
  const elapsedMin = Math.max(0, (totalWorkedMinutes || 0) + (isCheckedOut ? 0 : tick * 0.5))
  const progress = Math.min(elapsedMin / totalShift, 1)
  const overtime = Math.max(0, elapsedMin - totalShift)
  const overtimeProgress = overtime > 0 ? Math.min(overtime / totalShift, 1) : 0

  const elapsedH = Math.floor(elapsedMin / 60)
  const elapsedM = Math.floor(elapsedMin % 60)
  const totalH = Math.floor(totalShift / 60)
  const totalM = totalShift % 60

  // SVG ring — larger size, responsive via container
  const size = 180
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * (1 - progress)

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* Background ring */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="#e5e7eb" strokeWidth={stroke}
          />
          {/* Progress ring */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none"
            stroke={idle ? '#e5e7eb' : progress >= 1 ? '#22c55e' : BRAND}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={idle ? circumference : dashOffset}
            className="transition-all duration-1000"
          />
          {/* Overtime ring */}
          {overtime > 0 && (
            <circle
              cx={size / 2} cy={size / 2} r={radius - stroke - 2}
              fill="none" stroke="#f97316" strokeWidth={stroke - 2}
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * (radius - stroke - 2)}
              strokeDashoffset={2 * Math.PI * (radius - stroke - 2) * (1 - overtimeProgress)}
              className="transition-all duration-1000"
            />
          )}
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {idle ? (
            <span className="text-2xl font-bold text-gray-300 leading-tight tracking-widest">--:--</span>
          ) : (
            <span className="text-xl font-bold text-gray-900 leading-tight">
              {elapsedH}h {elapsedM}m
            </span>
          )}
          <span className="text-xs text-gray-400">
            / {totalH}h{totalM > 0 ? ` ${totalM}m` : ''}
          </span>
          {overtime > 0 && (
            <span className="text-xs font-medium text-orange-500 mt-0.5">
              +{Math.floor(overtime / 60)}h {Math.floor(overtime % 60)}m OT
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Dashboard ─── */
export default function Dashboard() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [showLeaveMenu, setShowLeaveMenu] = useState(false)
  const leaveMenuRef = useRef<HTMLDivElement>(null)
  const checkInOut = useCheckInOut()
  const { status, isCheckedIn, isCheckedOut } = checkInOut

  useEffect(() => {
    if (!showLeaveMenu) return
    const handler = (e: MouseEvent) => {
      if (leaveMenuRef.current && !leaveMenuRef.current.contains(e.target as Node)) {
        setShowLeaveMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showLeaveMenu])

  const leaveMutation = useMutation({
    mutationFn: createLeave,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
      queryClient.invalidateQueries({ queryKey: ['leave'] })
      queryClient.invalidateQueries({ queryKey: ['leaves'] })
      setShowLeaveMenu(false)
      // Over a monthly WFH allowance the day is sent for approval instead of recorded.
      if (data?.pending_approval && data?.message) checkInOut.setPendingMsgFromServer(data.message)
    },
  })

  const today = isoLocal(new Date()) // local date — avoids UTC off-by-one for UTC+ users

  /* ── Statistics + report data ── */
  const { query } = useSearchStore()
  const [reportTab, setReportTab] = useState<'daily' | 'weekly'>('daily')
  const [reportDate, setReportDate] = useState(today)
  const [pageSize, setPageSize] = useState(5)
  const [page, setPage] = useState(1)
  const [viewDate, setViewDate] = useState(today)

  const monthStart = today.slice(0, 8) + '01'
  const { data: monthRecords = [] } = useQuery({
    queryKey: ['attendance', 'my-history', 'month', monthStart, today],
    queryFn: () => getMyHistory(monthStart, today),
  })

  const [rangeStart, rangeEnd] = reportTab === 'weekly' ? weekRange(reportDate) : [reportDate, reportDate]
  const { data: reportRecords = [], isFetching: reportLoading } = useQuery({
    queryKey: ['attendance', 'my-history', 'report', rangeStart, rangeEnd],
    queryFn: () => getMyHistory(rangeStart, rangeEnd),
  })

  const isToday = viewDate === today

  // Records for the day, and the week + month around the selected date (for stats).
  const [viewWkStart, viewWkEnd] = weekRange(viewDate)
  const [viewMoStart, viewMoEnd] = monthRange(viewDate)
  // Month view: the employee's own day-by-day breakdown for the viewed month.
  const viewMonthStart = viewMoStart
  const monthOverview = useQuery({
    queryKey: ['attendance', 'me', 'overview', user?.id, viewMoStart, viewMoEnd > today ? today : viewMoEnd],
    queryFn: () => getMyAttendanceOverview(viewMoStart, viewMoEnd > today ? today : viewMoEnd),
    enabled: !!user?.id,
  })
  const statsStart = viewWkStart < viewMoStart ? viewWkStart : viewMoStart
  const statsEndRaw = viewWkEnd > viewMoEnd ? viewWkEnd : viewMoEnd
  const statsEnd = statsEndRaw > today ? today : statsEndRaw
  const { data: statsRecords = [] } = useQuery({
    queryKey: ['attendance', 'my-history', 'stats', statsStart, statsEnd],
    queryFn: () => getMyHistory(statsStart, statsEnd),
  })

  const dayRecords = useMemo(
    () => statsRecords.filter((r) => r.date === viewDate),
    [statsRecords, viewDate],
  )
  const dayRecord = dayRecords[0]

  const dailyTarget = shiftMinutes(
    status?.effective_standup_time || user?.standup_time,
    status?.effective_eod_time || user?.eod_time,
  )
  const workdays = user?.shift_days?.length || 5
  const weeklyTarget = dailyTarget * workdays
  const monthlyTarget = dailyTarget * 22

  const stats = useMemo(() => {
    const sum = (from: string, to: string) =>
      statsRecords
        .filter((r) => r.date >= from && r.date <= to)
        .reduce((acc, r) => acc + (r.duration_minutes || 0), 0)
    const dayMin = isToday
      ? (status?.total_worked_minutes ?? sum(viewDate, viewDate))
      : sum(viewDate, viewDate)
    const weekMin = sum(viewWkStart, viewWkEnd > today ? today : viewWkEnd)
    const monthMin = sum(viewMoStart, viewMoEnd > today ? today : viewMoEnd)
    return {
      dayMin,
      weekMin,
      monthMin,
      remainingMin: Math.max(0, dailyTarget - dayMin),
      overtimeMin: Math.max(0, dayMin - dailyTarget),
    }
  }, [statsRecords, status, isToday, viewDate, viewWkStart, viewWkEnd, viewMoStart, viewMoEnd, today, dailyTarget])
  const activityEvents = useMemo(() => {
    // Build from every session on the day so multiple check-ins/outs show in sequence.
    const events: { label: string; time?: string; tone: 'in' | 'out' }[] = []
    for (const r of dayRecords) {
      if (r.checkin_time) events.push({ label: 'Check In', time: r.checkin_time, tone: 'in' })
      if (r.checkout_time) events.push({ label: 'Check Out', time: r.checkout_time, tone: 'out' })
    }
    // Fallback: reflect a just-happened live check-in that isn't in history yet.
    if (isToday && events.length === 0 && status?.checkin_time) {
      events.push({ label: 'Check In', time: status.checkin_time, tone: 'in' })
      if (status?.checkout_time) events.push({ label: 'Check Out', time: status.checkout_time, tone: 'out' })
    }
    return events.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
  }, [dayRecords, isToday, status])

  const shiftDay = (delta: number) => {
    const [y, m, d] = viewDate.split('-').map(Number)
    const dt = new Date(y, m - 1, d + delta) // local, no UTC conversion
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
    if (iso <= today) setViewDate(iso)
  }
  const viewDateLabel = new Date(viewDate + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', day: 'numeric', month: 'short',
  })
  const viewDateFull = new Date(viewDate + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  const filteredReport = useMemo(() => {
    const q = query.trim().toLowerCase()
    const name = user?.name || ''
    if (!q) return reportRecords
    // When searching, widen to the whole month so the query has data to match.
    return monthRecords.filter((r) =>
      [name, r.date, r.status, formatTime(r.checkin_time), formatTime(r.checkout_time)]
        .join(' ').toLowerCase().includes(q),
    )
  }, [reportRecords, monthRecords, query, user])

  useEffect(() => { setPage(1) }, [reportTab, reportDate, pageSize, query])
  const totalRows = filteredReport.length
  const pageRows = filteredReport.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))

  const dateLabel = new Date(today + 'T00:00:00').toLocaleDateString('en-US', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className="space-y-6">
      {checkInOut.pendingMsg && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="flex-1 text-sm text-amber-800">{checkInOut.pendingMsg}</p>
          <button onClick={checkInOut.clearPendingMsg} className="text-amber-500 hover:text-amber-700"><X className="h-4 w-4" /></button>
        </div>
      )}
      {/* Page header + day switcher */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Attendance</h1>
          <p className="text-sm text-gray-500">Check in and out, and review your hours and history.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => shiftDay(-1)} title="Previous day" className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="relative">
            <Calendar className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={viewDate}
              max={today}
              onChange={(e) => e.target.value && e.target.value <= today && setViewDate(e.target.value)}
              className="rounded-lg border border-gray-300 pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40"
            />
          </div>
          <button onClick={() => shiftDay(1)} disabled={isToday} title="Next day" className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40">
            <ChevronRight className="h-4 w-4" />
          </button>
          {!isToday && (
            <button onClick={() => setViewDate(today)} className="rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#d54e22]">Today</button>
          )}
        </div>
      </div>

      {/* Top: 3 cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch">
        {/* Clock / check-in card */}
        <Card title={<>Date: <span className="font-normal text-gray-500">{isToday ? dateLabel : viewDateFull}</span></>}>
          <div className="flex flex-col items-center gap-5 py-1">
            <div className="w-full text-center min-h-[2.5rem]">
              {isToday ? (
                <>
                  {checkInOut.statusLoading ? (
                    <p className="text-sm text-gray-400">Loading…</p>
                  ) : isCheckedOut ? (
                    <>
                      <p className="text-sm font-medium text-gray-700">Last session completed</p>
                      <p className="text-xs text-gray-400">{formatTime(status?.checkin_time)} — {formatTime(status?.checkout_time)} · {calcDuration(status?.checkin_time, status?.checkout_time)}</p>
                    </>
                  ) : isCheckedIn ? (
                    <>
                      <p className="text-sm font-medium text-green-600">Checked in</p>
                      <p className="text-xs text-gray-400">since {formatTime(status?.checkin_time)}</p>
                    </>
                  ) : status?.getting_late ? (
                    <>
                      <p className="text-sm font-medium text-amber-600">Getting late</p>
                      <p className="text-xs text-gray-400">Expected at {status?.effective_standup_time || '—'}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-700">Not checked in yet</p>
                      <p className="text-xs text-gray-400">Start your day by checking in</p>
                    </>
                  )}
                  {status?.is_late && (isCheckedIn || isCheckedOut) && (
                    <p className="mt-1 text-xs font-medium text-amber-500">
                      Late by {status.late_minutes && status.late_minutes >= 60 ? `${Math.floor(status.late_minutes / 60)}h ${status.late_minutes % 60}m` : `${status.late_minutes}m`}
                    </p>
                  )}
                </>
              ) : dayRecord ? (
                <>
                  <p className="text-sm font-medium text-gray-700 capitalize">{(dayRecord.status || 'present').replace('_', ' ')}</p>
                  <p className="text-xs text-gray-400">
                    {formatTime(dayRecord.checkin_time)}{dayRecord.checkout_time ? ` — ${formatTime(dayRecord.checkout_time)}` : ''}
                    {dayRecord.duration_minutes ? ` · ${hoursMins(dayRecord.duration_minutes)}` : ''}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-700">No attendance recorded</p>
                  <p className="text-xs text-gray-400">Nothing logged for this day.</p>
                </>
              )}
            </div>

            <ShiftClock
              idle={isToday ? (!isCheckedIn && !isCheckedOut) : stats.dayMin === 0}
              isCheckedOut={isToday ? (isCheckedOut || false) : true}
              // Cumulative worked minutes across ALL of the day's sessions (not just the first).
              totalWorkedMinutes={stats.dayMin}
              effectiveStandupTime={status?.effective_standup_time}
              effectiveEodTime={status?.effective_eod_time}
            />

            {isToday ? (
              <>
                <div className="flex flex-col sm:flex-row gap-2.5 w-full">
                  {isCheckedIn ? (
                    <button onClick={checkInOut.openCheckout} className={`${btnDanger} flex-1`}>
                      <LogOut className="h-4 w-4" /> Check Out
                    </button>
                  ) : (
                    <>
                      <button onClick={() => checkInOut.openCheckin()} className={`${btnPrimary} flex-1`}>
                        <LogIn className="h-4 w-4" /> Check In
                      </button>
                      <button onClick={() => checkInOut.openCheckin(true)} className={`${btnSecondary} flex-1`}>
                        <Home className="h-4 w-4" /> Check In WFH
                      </button>
                    </>
                  )}
                </div>

                <div className="relative w-full" ref={leaveMenuRef}>
                  <button
                    onClick={() => setShowLeaveMenu(!showLeaveMenu)}
                    disabled={leaveMutation.isPending}
                    className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-[#EC5B2C] hover:bg-orange-50 disabled:opacity-50"
                  >
                    <Calendar className="h-4 w-4" /> Inform Leave Today
                  </button>
                  {showLeaveMenu && (
                    <div className="absolute bottom-full left-0 mb-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {(['casual', 'sick', 'urgent'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => {
                            setShowLeaveMenu(false)
                            if (isCheckedIn) {
                              checkInOut.setPendingLeaveType(type)
                              checkInOut.forceOpenCheckout()
                            } else {
                              leaveMutation.mutate({ date: today, type })
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {type === 'casual' && '🏖️ Casual Leave'}
                          {type === 'sick' && '🤒 Sick Leave'}
                          {type === 'urgent' && '🚨 Urgent Leave'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {leaveMutation.isError && <p className="text-xs text-red-600">Failed — you may already have a record for today.</p>}
                {leaveMutation.isSuccess && <p className="text-xs text-green-600">Recorded successfully.</p>}
              </>
            ) : (
              <button onClick={() => setViewDate(today)} className={`${btnSecondary} w-full`}>Back to today</button>
            )}
          </div>
        </Card>

        {/* Statistics */}
        <Card title="Statistics">
          <div className="h-full flex flex-col justify-between gap-5">
            <StatBar label={isToday ? 'Today' : viewDateLabel} value={stats.dayMin} target={dailyTarget} color="#EC5B2C" />
            <StatBar label="Week" value={stats.weekMin} target={weeklyTarget} color="#F7A94B" />
            <StatBar label="Month" value={stats.monthMin} target={monthlyTarget} color="#22c55e" />
            <StatBar label="Remaining" value={stats.remainingMin} target={dailyTarget} color="#ef4444" hideTarget />
            <StatBar label="Overtime" value={stats.overtimeMin} target={dailyTarget} color="#14b8a6" hideTarget />
          </div>
        </Card>

        {/* Activity timeline */}
        <Card title={isToday ? 'Today Activity' : `Activity · ${viewDateLabel}`}>
          {activityEvents.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">No activity for this day.</p>
          ) : (
            <div className="overflow-y-auto pl-1.5 max-h-80 lg:max-h-none lg:absolute lg:inset-0">
              <ol className="relative ml-1.5 border-l-2 border-gray-100 space-y-6 py-1">
                {activityEvents.map((e, i) => (
                  <li key={i} className="ml-5 relative">
                    <span className={`absolute -left-[1.6rem] top-0.5 h-3.5 w-3.5 rounded-full ring-4 ring-white ${e.tone === 'in' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <p className="text-sm font-medium text-gray-800">{e.label}</p>
                    <p className="text-xs text-gray-400">{formatTime(e.time)}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Card>
      </div>

      {/* Month view — one coloured cell per day */}
      <Card title={<>Month View: <span className="font-normal text-gray-500">{format(parseISO(viewMonthStart), 'MMMM yyyy')}</span></>}>
        {monthOverview.isLoading
          ? <p className="py-8 text-center text-sm text-gray-400">Loading…</p>
          : <AttendanceCalendar month={parseISO(viewMonthStart)} days={monthOverview.data?.days || []} />}
      </Card>

      {/* Attendance Report */}
      <Card title="Attendance Report">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {(['daily', 'weekly'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setReportTab(t)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${reportTab === t ? 'bg-[#EC5B2C] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >{t}</button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="rounded-lg border border-gray-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40"
              />
            </div>
            <label className="flex items-center gap-1.5 text-sm text-gray-500">
              Show
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              >
                {[5, 10, 25].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              entries
            </label>
          </div>
        </div>
        {query.trim() ? (
          <p className="text-xs text-[#EC5B2C] mb-3">Showing matches for “{query.trim()}” across this month.</p>
        ) : reportTab === 'weekly' && (
          <p className="text-xs text-gray-400 mb-3">Week of {rangeStart} → {rangeEnd}</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="text-left text-gray-500 border-b border-gray-100">
              <tr>
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Date</th>
                <th className="px-3 py-2.5 font-medium">Check In</th>
                <th className="px-3 py-2.5 font-medium">Check Out</th>
                <th className="px-3 py-2.5 font-medium">Duration</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reportLoading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No records for this period.</td></tr>
              ) : (
                pageRows.map((r: MyAttendanceRecord, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-medium text-gray-800">{user?.name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{new Date(r.date + 'T00:00:00').toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 text-gray-600">{formatTime(r.checkin_time)}</td>
                    <td className="px-3 py-2.5 text-gray-600">{formatTime(r.checkout_time)}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.duration_minutes ? hoursMins(r.duration_minutes) : '—'}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-4">
          <p className="text-sm text-gray-500">
            {totalRows === 0 ? 'No entries' : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, totalRows)} of ${totalRows} entries`}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >Previous</button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#d54e22] disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      </Card>

      {/* Check-in Modal */}
      {checkInOut.showCheckin && checkInOut.checkinData && !checkInOut.checkinData.already_checked_in && (
        <CheckinModal
          carryoverGoals={checkInOut.checkinData.carryover_goals}
          onSubmit={checkInOut.submitCheckin}
          onClose={checkInOut.closeCheckin}
          isPending={checkInOut.checkinMutation.isPending}
        />
      )}

      {/* Early Leave Prompt */}
      {checkInOut.showEarlyLeavePrompt && (
        <EarlyLeavePrompt
          eodTime={status?.effective_eod_time || user?.eod_time?.slice(0, 5) || '17:00'}
          reason={checkInOut.earlyLeaveReason}
          onReasonChange={checkInOut.setEarlyLeaveReason}
          onCancel={checkInOut.cancelEarlyLeave}
          onContinue={checkInOut.confirmEarlyLeave}
        />
      )}

      {/* Check-out Modal */}
      {checkInOut.showCheckout && checkInOut.checkoutData && checkInOut.checkoutData.has_active_checkin && (
        <CheckoutModal
          todaysGoals={checkInOut.checkoutData.todays_goals}
          onSubmit={checkInOut.submitCheckout}
          onClose={checkInOut.closeCheckout}
          isPending={checkInOut.checkoutMutation.isPending}
          staleDate={checkInOut.checkoutData.stale_session_date}
          staleDefaultTime={checkInOut.checkoutData.stale_default_time}
          error={(checkInOut.checkoutMutation.error as any)?.response?.data?.detail}
        />
      )}
    </div>
  )
}

/* ─── Reusable card (same shell as the dashboard SectionCard: header bar + body) ─── */
function Card({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col">
      <h2 className="text-sm font-semibold text-gray-800 px-4 sm:px-5 py-3.5 border-b border-gray-100">{title}</h2>
      <div className="flex-1 min-h-0 relative p-4 sm:p-5">{children}</div>
    </div>
  )
}

/* ─── Statistics progress bar ─── */
function StatBar({ label, value, target, color, hideTarget }: {
  label: string; value: number; target: number; color: string; hideTarget?: boolean
}) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[15px] font-medium text-gray-700">{label}</span>
        <span className="text-sm font-semibold text-gray-500">
          {hoursMins(value)}{hideTarget ? '' : ` / ${Math.round(target / 60)} hrs`}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  )
}

/* ─── Attendance status badge ─── */
function StatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase()
  const map: Record<string, string> = {
    present: 'bg-green-100 text-green-700',
    late: 'bg-amber-100 text-amber-700',
    wfh: 'bg-teal-100 text-teal-700',
    absent: 'bg-red-100 text-red-700',
    leave: 'bg-purple-100 text-purple-700',
    half_day: 'bg-blue-100 text-blue-700',
  }
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${map[s] || 'bg-gray-100 text-gray-600'}`}>
      {s.replace('_', ' ') || '—'}
    </span>
  )
}
